import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { SvgIcon } from 'common/react/components/SvgIcon';
import { LazyImage } from 'common/react/components/LazyImage';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { selectCardHandler } from './util';

/**
 * Renders the MediaThumbnail for a news article.
 *
 * @param {string} title - The widget title.
 * @param {object} article - The club news article data.
 * @param {string} compIcon - The competition icon.
 * @param {string} tags - The tags to provide as tag labels.
 * @returns {JSX.Element} Component.
 */

const BASE_URL_PATH = 'news';

export const MediaThumbnailArticle = ({ article, index, compIcon, tags }) => {
    const tagLabels = compIcon
        ? article.tags.map((articleTag) =>
              tags.includes(articleTag.label) ? articleTag.label : null
          )
        : null;

    const refs = article.references;
    const compId = refs.find((ref) => ref.type === 'AFL_COMPETITION')?.sid;
    const compAbbr = PULSE.I18N.lookup(
        `label.AFL_COMPETITION.${compId}`
    ).toLowerCase();

    // Responsive
    const windowSize = useWindowSize();

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.phablet;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
    }, [windowSize.width]);

    return (
        <article className={`media-thumbnail`}>
            <div className={`media-thumbnail__link`}>
                <a
                    className={`media-thumbnail__absolute-link js-media-thumbnail__link`}
                    href={`/${BASE_URL_PATH}/${article?.id}/${article?.titleUrlSegment}`}
                    title={article?.title}
                    aria-label={article?.title}
                    data-index-position={index + 1}
                    onClick={(evt) => selectCardHandler(evt, article?.title)}
                ></a>
                <header
                    className={`media-thumbnail__image-container placeholder placeholder--small placeholder--dark`}
                >
                    <div
                        className={`js-lazy-load lazy-image-wrapper is-loaded`}
                    >
                        <LazyImage
                            url={article?.imageUrl}
                            cssClass="js-faded-image fade-in-on-load object-fit-cover-picture__img is-loaded"
                        />
                    </div>
                </header>
                <div className={`media-thumbnail__caption`}>
                    <h3 className={`media-thumbnail__title`}>
                        {article?.title}
                    </h3>
                    <p className={`media-thumbnail__description`}>
                        {article?.description}
                    </p>
                    {!showResponsiveItems ? (
                        <div className={`media-thumbnail__footer`}>
                            <div
                                className={`media-thumbnail__reference ${
                                    compIcon
                                        ? `media-thumbnail__reference--${compAbbr}`
                                        : null
                                }`}
                            >
                                <div className="media-thumbnail__comp-logo">
                                    <SvgIcon
                                        icon={`${compAbbr}-logo${
                                            compAbbr !== 'afl' ? '-white' : ''
                                        }`}
                                        className={`icon icn-${compAbbr}`}
                                    />
                                </div>
                                {/* Only display the chevron and label if there is a matching tag label on the content item */}
                                {tagLabels.map((tagLabel, tagIndex) =>
                                    tagLabel ? (
                                        <React.Fragment
                                            key={`media-thumbnail_article-${tagIndex}`}
                                        >
                                            <SvgIcon
                                                icon={`chevron-right`}
                                                className={`icon media-thumbnail__chevron`}
                                            />
                                            <div
                                                className={`media-thumbnail__tag-label media-thumbnail__tag-label--capitalize`}
                                            >
                                                {tagLabel}
                                            </div>
                                        </React.Fragment>
                                    ) : null
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
            {showResponsiveItems ? (
                <div className={`media-thumbnail__footer`}>
                    <div
                        className={`media-thumbnail__reference ${
                            compIcon
                                ? `media-thumbnail__reference--${compAbbr}`
                                : null
                        }`}
                    >
                        <div className="media-thumbnail__comp-logo">
                            <SvgIcon
                                icon={`${compAbbr}-logo${
                                    compAbbr !== 'afl' ? '-white' : ''
                                }`}
                                className={`icon icn-${compAbbr}`}
                            />
                        </div>
                        {/* Only display the chevron and label if there is a matching tag label on the content item */}
                        {tagLabels.map((tagLabel, tagIndex) =>
                            tagLabel ? (
                                <React.Fragment
                                    key={`media-thumbnail_article-${tagIndex}`}
                                >
                                    <SvgIcon
                                        icon={`chevron-right`}
                                        className={`icon media-thumbnail__chevron`}
                                    />
                                    <div
                                        className={`media-thumbnail__tag-label media-thumbnail__tag-label--capitalize`}
                                    >
                                        {tagLabel}
                                    </div>
                                </React.Fragment>
                            ) : null
                        )}
                    </div>
                </div>
            ) : null}
        </article>
    );
};

MediaThumbnailArticle.propTypes = {
    article: PropTypes.object,
    index: PropTypes.number,
    compIcon: PropTypes.string,
    tags: PropTypes.string
};
